import { Pets, IPet, PetType, CoverType } from "./common-types";
import {
  getGuid,
  getFlowCache,
  getFlowPathData,
  getFlowDataDirect,
  setFlowDataDirect,
  getRecaptchaToken,
  logEvent,
  getSavedQuote,
  sendSms,
  checkQuoteExpired,
  getPromotionCode,
} from "@adaptis/flow-components";
import { getCheckDigit, isCheckdigitCorrect } from "./helper-functions";
import _ from "../imports/lodash-imports";
import { loadBreedsList, issueBatchApiWrapper } from "./knose-api-wrapper";
import { getPetAge, getDiffDate } from "../KnoseAPI/Lib/Utils";
import Config from "../app-config";
import * as API from "../KnoseAPI/Actions/api";
import tr from "@mobiscroll/react/dist/src/i18n/tr";
import moment from "dayjs";

const ordinalNames = [
  "first",
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "ninth",
  "tenth",
  "eleventh",
  "twelfth",
  "thirteenth",
  "fourteenth",
  "fifteenth",
  "sixteenth",
  "seventeenth",
  "eighteenth",
  "nineteenth",
  "twentieth",
];

export function checkPetNo(pets: Pets, pet_no: number) {
  if (!Array.isArray(pets)) return false;  // nếu pets null || undefined thì trả về false luôn (Fix sentry error)
  return pets.some((pet) => pet.pet_no == pet_no);
}

function normalizePets(pets: any): Pets {
  if (Array.isArray(pets)) return pets;
  if (pets && typeof pets === "object") {
    return Object.values(pets).filter((pet: any) => !!pet && typeof pet === "object") as Pets;
  }
  return [];
}

export function addNewPet(pets: Pets, petType: PetType) {
  const normalizedPets = normalizePets(pets);
  let findedNewPetNo = false;
  let pet_no = normalizedPets.length;
  while (!findedNewPetNo) {
    let result = checkPetNo(normalizedPets, pet_no);
    if (!result) {
      findedNewPetNo = true;
    } else {
      pet_no++;
    }
  }
  let len = normalizedPets.push({
    id: getGuid(),
    pet_no,
    petType: petType,
    petTypeIndex: -1,
    petOrdinalName: "",
    isShowedPopup: false,
    name: "",
    pet_refs: {
      callReferenceId: moment().unix().toString(),
    },
  });

  // Add computed properties
  Object.defineProperty(normalizedPets[len - 1], "ageLong", {
    get: function () {
      if (this?.petDOB) return getPetAge(this.petDOB).pet_age;
      else return "---";
    },
  });

  // Sort dogs then cats
  normalizedPets.sort((a, b) => {
    if (a.petType === PetType.Dog && b.petType === PetType.Cat) {
      return -1;
    }
    if (a.petType === PetType.Cat && b.petType === PetType.Dog) {
      return 1;
    }
    return 0;
  });

  // Update petTypeIndex & petOrdinalName
  let petTypeIndex = 0;
  let currentPetType = PetType.Dog;
  normalizedPets.forEach((pet, index) => {
    if (pet.petType !== currentPetType) {
      currentPetType = pet.petType;
      petTypeIndex = 0;
    }
    pet.petTypeIndex = petTypeIndex;
    pet.petOrdinalName = ordinalNames[petTypeIndex];
    petTypeIndex++;
  });

  if (normalizedPets !== pets) {
    setFlowDataDirect("pets", normalizedPets);
  }

  return normalizedPets;
}

export function removeLastPet(pets: Pets, petType: PetType) {
  let lastAnimalIndex: number = -1;

  for (let i = pets.length - 1; i >= 0; i--) {
    if (pets[i].petType === petType) {
      lastAnimalIndex = i;
      break;
    }
  }

  if (lastAnimalIndex !== -1) {
    pets.splice(lastAnimalIndex, 1);
  }

  return pets;
}

export function removePet(pets: Pets, petId: string) {
  let index: number = -1;

  for (let i = 0; i < pets.length; i++) {
    if (pets[i].id === petId) {
      index = i;
      break;
    }
  }

  if (index !== -1) {
    pets.splice(index, 1);
  }

  return pets;
}

export function getPetQty(pets: Pets): {
  dogQty: number;
  catQty: number;
  total: number;
} {
  let dogQty: number = 0;
  let catQty: number = 0;

  if (!pets) {
    return {
      dogQty: 0,
      catQty: 0,
      total: 0,
    };
  }

  // Nếu pets là mảng
  if (Array.isArray(pets)) {
    pets.forEach((animal) => {
      if (animal.petType === PetType.Dog) dogQty++;
      else catQty++;
    });
  }
  // Nếu pets là object
  else if (pets && typeof pets === "object") {
    Object.values(pets).forEach((animal: any) => {
      if (animal.petType === PetType.Dog) dogQty++;
      else catQty++;
    });
  }

  return {
    dogQty: dogQty,
    catQty: catQty,
    total: dogQty + catQty,
  };
}

/*
  petType: PetType;
  petTypeIndex: number;
  petOrdinalName: string;
  name: string;
  breed?: string;
  petDOB?: string;
  startDate?: string;
  petSex?: string;
  isDesexed?: boolean;
  promoCode?: string;
  benefitPercentage?: string;
  annualBenefitLimit?: string;
  annualExcess?: string;
  optionalExtras?: string;
*/
export function isPetValid(pet: IPet) {
  if (pet.name.length === 0) {
    return false;
  }
  if (!pet.breed || pet.breed.length === 0) {
    return false;
  }
  if (!pet.petDOB) {
    return false;
  }
  if (!pet.startDate) {
    return false;
  }
  if (!pet.petSex || pet.petSex.length === 0) {
    return false;
  }
  if (!pet.isDesexed) {
    return false;
  }
  if (!pet.coverType) {
    return false;
  }
  if (pet.coverType && pet.coverType === CoverType.Custom) {
    if (!pet?.customCover?.benefitPercentage || pet?.customCover?.benefitPercentage.length === 0) {
      return false;
    }
    if (
      !pet?.customCover?.annualBenefitLimit ||
      pet?.customCover?.annualBenefitLimit.length === 0
    ) {
      return false;
    }
    if (!pet?.customCover?.annualExcess || pet?.customCover?.annualExcess.length === 0) {
      return false;
    }
    if (!pet?.customCover?.optionalExtras || pet?.customCover?.optionalExtras.length === 0) {
      return false;
    }
    if (
      !pet?.customCover?.vetConsultationFees ||
      pet?.customCover?.vetConsultationFees.length === 0
    ) {
      return false;
    }
  }
  return true;
}

export function getProductType(pet: IPet) {
  if (pet.coverType) return pet.coverType === CoverType.Top ? "Top Cover" : "Custom Cover";
  else return "";
}

export async function getBreedsList(petType: PetType) {
  // Check cache and return from there if loaded
  let flowCacheStore = getFlowCache();
  let breedsList = _.get(flowCacheStore, "breedsList");

  // If not loaded, load from API and cache
  if (!breedsList) {
    let breedsListAPI = await loadBreedsList();

    breedsList = {
      attributeOptions: [],
    };
    if (breedsListAPI && breedsListAPI.data) {
      breedsListAPI.data.forEach((breed: { breed_name: string; pet_type: string }) => {
        let sub: any = {
          displayName: breed.breed_name,
          filterGroup: breed.pet_type,
        };
        breedsList.attributeOptions.push(sub);
      });
    }
    _.set(flowCacheStore, "breedsList", breedsList);
  }

  return breedsList.attributeOptions.filter(
    (breed: any) => breed.filterGroup === _.capitalize(petType),
  );
}

export function getPetOriginalModel() {
  let flowCacheStore = getFlowCache();
  let petOriginalModel = _.get(flowCacheStore, "petOriginalModel");

  return petOriginalModel;
}

export function setPetOriginalModel(pets: any) {
  let flowCacheStore = getFlowCache();
  _.set(flowCacheStore, "petOriginalModel", pets);
}

export function getOriginalPostCode() {
  let flowCacheStore = getFlowCache();
  let originalPostCode = _.get(flowCacheStore, "originalPostCode");

  return originalPostCode;
}

export function setOriginalPostCode(postcode: any) {
  let flowCacheStore = getFlowCache();
  _.set(flowCacheStore, "originalPostCode", postcode);
}

export function getPaymentFrequencyLabel(paymentFrequency: string) {
  switch (paymentFrequency.toLowerCase()) {
    case "fortnightly":
      return "fortnight";
    case "monthly":
      return "month";
    case "annually":
      return "year";
    default:
      return "";
  }
}

function getFirstMonthDiscounted(data: any): number {
  // Tìm đối tượng có giai đoạn chứa tháng đầu tiên (from = 1)
  const firstPeriod = data.find((item: any) => item.from === 1);

  if (!firstPeriod) {
    console.log("Not found period start from month 1");
    return -1;
  }

  // Tổng số tiền cần trả trong giai đoạn
  const { totalPayable, totalBaseAmount, from, to } = firstPeriod;

  // Tính số tiền cần trả cho tháng đầu tiên
  // return totalPayable / totalMonths;
  return totalPayable;
}

export function updateTotalAmounts() {
  // get the pet
  let wasPriceByYear: number = 0; // Tổng số tiền hằng năm
  let discountedPriceByYear: number = 0; // Giá sau giảm giá tính theo năm
  let totalDiscountedByYear: number = 0; // Tổng số tiền được giảm theo năm
  let firstMonthPrice: number = 0; // Tổng số tiền được giảm thang dau tien
  let installmentAmount: number = 0;
  let installmentBasePremium: number = 0;
  let installmentBasePremiumPlusGST: number = 0;
  let installmentStampDuty: number = 0;
  let installmentGST: number = 0;
  let installmentPolicyFee: number = 0;
  let installmentPolicyFeeTotal: number = 0;
  let totalInstallmentBeforeDiscount: number = 0;
  let totalInstallmentDiscountAmount: number = 0;
  let totalPayToday: number = 0;
  let allPetnames: string = "";

  let pets = getFlowDataDirect("pets");

  if (!pets || !Array.isArray(pets)) return;

  pets.forEach((pet: any) => {
    const coverType = pet.coverType + "Cover";
    if (pet.coverType != undefined) {
      let cover = pet[coverType] ?? {};
      let premiums = cover?.premiums ?? {};
      installmentAmount += premiums?.installment ?? 0; // installmentAmount
      installmentBasePremium += premiums?.installmentBasePremium ?? 0; // installmentBasePremium
      installmentBasePremiumPlusGST += premiums?.premium ?? 0; // installmentBasePremiumPlusGST
      installmentStampDuty += premiums?.installmentStampDuty ?? 0; // installmentStampDuty
      installmentGST += premiums?.premiumGST ?? 0; // installmentGST
      installmentPolicyFee += premiums?.policy_fee ?? 0; // installmentPolicyFee *
      installmentPolicyFeeTotal += premiums?.policy_fee ?? 0; // installmentPolicyFeeTotal
      totalInstallmentBeforeDiscount += premiums?.installment_before_discount ?? 0; // totalInstallmentBeforeDiscount
      totalInstallmentDiscountAmount += premiums?.totalInstallmentDiscountAmount ?? 0; //totalInstallmentDiscountAmount *
      if (allPetnames.length === 0) {
        allPetnames = pet.name;
      } else {
        allPetnames = allPetnames + ", " + pet.name;
      }

      if (["2", "type2"].includes(cover?.promo?.promoType?.toString().toLowerCase())) {
        firstMonthPrice += getFirstMonthDiscounted(
          cover?.promo?.policyPremiumInstallmentPayments ?? [],
        );
      } else {
        firstMonthPrice += (premiums?.totalPayable ?? 0) / 12;
      }
      wasPriceByYear += premiums?.totalPayableBeforeDiscount ?? 0;
      discountedPriceByYear += premiums?.totalPayable ?? 0;
    }
  });
  totalDiscountedByYear = wasPriceByYear - discountedPriceByYear;
  totalPayToday = Math.max(firstMonthPrice, 0);
  // Makes allPetnames look like 'Pet1, Pet2 & Pet3' or 'Pet1 & Pet2'
  let n = allPetnames.lastIndexOf(", ");
  allPetnames = allPetnames.slice(0, n) + allPetnames.slice(n).replace(", ", " & ");

  setFlowDataDirect("totalAmounts.installmentAmount", _.round(installmentAmount, 2));
  setFlowDataDirect("totalAmounts.installmentBasePremium", _.round(installmentBasePremium, 2));
  setFlowDataDirect(
    "totalAmounts.installmentBasePremiumPlusGST",
    _.round(installmentBasePremiumPlusGST, 2),
  );
  setFlowDataDirect("totalAmounts.installmentStampDuty", _.round(installmentStampDuty, 2));
  setFlowDataDirect("totalAmounts.installmentGST", _.round(installmentGST, 2));
  setFlowDataDirect("totalAmounts.installmentPolicyFee", _.round(installmentPolicyFee, 2));
  setFlowDataDirect(
    "totalAmounts.installmentPolicyFeeTotal",
    _.round(installmentPolicyFeeTotal, 2),
  );
  setFlowDataDirect(
    "totalAmounts.totalInstallmentBeforeDiscount",
    _.round(totalInstallmentBeforeDiscount, 2),
  );

  setFlowDataDirect(
    "totalAmounts.totalInstallmentDiscountAmount",
    _.round(totalInstallmentDiscountAmount, 2),
  );
  setFlowDataDirect("totalAmounts.totalDiscountedByYear", _.round(totalDiscountedByYear, 2));
  setFlowDataDirect("totalAmounts.discountedPriceByYear", _.round(discountedPriceByYear, 2));
  setFlowDataDirect("totalAmounts.wasPriceByYear", _.round(wasPriceByYear, 2));
  setFlowDataDirect("totalAmounts.totalPayToday", _.round(totalPayToday, 2));
  setFlowDataDirect("totalAmounts.allPetNames", allPetnames);
}

export function getTotalPrice() {
  updateTotalAmounts();

  let price = {
    installmentAmount: 0,
    totalInstallmentBeforeDiscount: 0,
  };

  price.installmentAmount = getFlowDataDirect("totalAmounts.installmentAmount");
  price.totalInstallmentBeforeDiscount = getFlowDataDirect(
    "totalAmounts.totalInstallmentBeforeDiscount",
  );

  return price;
}

export async function getSalesJourneyForQuotes() {
  let salesJourneyId = getFlowDataDirect("salesJourneyId");
  if (salesJourneyId == undefined || salesJourneyId == null || salesJourneyId == "") {
    salesJourneyId = await generateSalesJourneyForQuotes();
    //setFlowDataDirect('pets[0].'+coverType+'Cover.salesJourneyId',salesJourneyId);
    setFlowDataDirect("salesJourneyId", salesJourneyId);
  }
  return salesJourneyId;
}

// will generate the sales journey for the quotes in the data store, and save
export async function generateSalesJourneyForQuotes() {
  const issuesParams = await getIssuesBatchRequest();
  console.log("issuesParams :>> ", issuesParams);
  let issuesResult: any = await issueBatchApiWrapper(issuesParams);
  console.log("issuesResult :>> ", issuesResult);
  const firstQuoteSyncId: number = getFirstQuoteSyncId();
  const quoteDetails = issuesResult[firstQuoteSyncId];
  return quoteDetails.salesJourneyId;
}

function getFirstQuoteSyncId() {
  const pets = getFlowDataDirect("pets");
  const pet = pets[0];
  const coverType = pet.coverType + "Cover";
  const quotationSyncId = pet[coverType].quotationSyncId;
  return quotationSyncId;
}

async function getIssuesBatchRequest() {
  const pets: Array<IPet> = getFlowDataDirect("pets");

  const tokenParams = await getRecaptchaToken();
  let obj: any = {};
  pets.forEach((pet) => {
    let coverType: CoverType | undefined = pet.coverType;
    let quoteSync: number | undefined = 0;
    if (coverType == CoverType.Top) {
      quoteSync = pet.topCover?.quotationSyncId;
    } else {
      quoteSync = pet.customCover?.quotationSyncId;
    }
    let quoteSyncIndex: number = quoteSync == undefined ? 0 : quoteSync;
    let innerObject = { quotationSyncId: quoteSync };
    obj[quoteSyncIndex] = innerObject;
  });
  obj["captcha"] = tokenParams;
  return obj;
}

export async function logQuote(
  origamiQuoteNumber: any,
  origamiPetData: any,
  origamiQuote: any,
): Promise<string> {
  let result: any;
  let quoteNo: string = "";

  await logEvent("quote", true, origamiQuoteNumber, origamiPetData, origamiQuote)
    .then(function (response: any) {
      return response.json();
    })
    .then(function (data: any) {
      result = data.eventId;
    });

  if (_.isNumber(result)) {
    quoteNo = "Q" + result.toString() + getCheckDigit(result.toString());
    setFlowDataDirect("quoteNo", quoteNo);
  }

  return quoteNo;
}

// export async function sendQuoteEmail() {
//   let salesJourneyId = await generateSalesJourneyForQuotes();
//   let token = await getRecaptchaToken();
//   setFlowDataDirect('salesJourneyId', salesJourneyId);
//   console.log('salesJourneyId :>> ', salesJourneyId);

//   let result = await API.knoseAPI(Config.action_quote_email + salesJourneyId, {
//     captcha: token,
//   });

//   console.log('email quote result :>> ', result);

//   let isSuccess = result ? true : false;
//   return isSuccess;
// }

export async function loadSavedQuote(quoteNo: string): Promise<any> {
  function isNumeric(value: string) {
    return /^-?\d+$/.test(value);
  }

  let parsedQuoteNo = quoteNo.toLocaleUpperCase().replace("Q", "").replace(" ", "");

  if (!isNumeric(parsedQuoteNo) || !isCheckdigitCorrect(parsedQuoteNo))
    return Promise.resolve(false);

  let quote = await getSavedQuote(parsedQuoteNo.slice(0, -1));
  let sessionData = JSON.parse(quote.sessionData);
  if (quote && sessionData) {
    // check quote expired
    const checkDate = getDiffDate(30);
    let isExpired = true;
    try {
      // console.log("PPP-53-1");
      let quotationNumber: any[] = [];
      sessionData.pets.forEach((pet: any, index: number) => {
        if (pet.policyDetailResponse && pet.policyDetailResponse.quotationNumber) {
          quotationNumber.push(pet.policyDetailResponse.quotationNumber);
        }
      });
      const getQuoteData: any = await checkQuoteExpired({
        quoteNo: quotationNumber,
        date: checkDate,
      });
      // console.log("PPP-53-2", getQuoteData);
      if (getQuoteData.quotes && getQuoteData.quotes.length) {
        isExpired = false;
      }
    } catch (error) {
      console.log("PPP-53-3");
    }

    let firstId = getGuid();
    if (isExpired) {
      // console.log("PPP-53-3.1", sessionData);
      sessionData.pets.forEach((pet: any, index: number) => {
        if (sessionData.pets[index].customCover) {
          sessionData.pets[index].customCover.quoteNumber = null;
          sessionData.pets[index].customCover.quotationSyncId = "";
          sessionData.pets[index].customCover.oldQuote = quoteNo;
        }

        if (!index) {
          sessionData.pets[index].id = firstId;
        } else {
          sessionData.pets[index].id = getGuid();
        }
      });
      sessionData["quote-slug"] = `/custom-cover/${firstId}`;
      sessionData.sessionId = getGuid();
      sessionData.quoteNo = "";
    }
    // console.log("follow data", getFlowPathData());
    const currentFollowRootData = getFlowPathData();
    sessionData["root"] = currentFollowRootData.root;

    //check valid promo code
    let promoCode = sessionData.promoCode;
    let listPromocodes = currentFollowRootData.root?.promotion?.code || [];
    if (promoCode && listPromocodes) {
      //check exist
      let checkValidCode = listPromocodes.find((item: any) => item.promoCode === promoCode);
      if (!checkValidCode) {
        sessionData.promoCode = null;
        sessionData.promoCodeType = 1;
      }
    }
    setFlowDataDirect("", sessionData);
    return Promise.resolve({ success: true, isExpired, sessionData });
  } else {
    return Promise.resolve({ success: false });
  }
}

export function setQuoteData(
  setData: (dataField: string, data: any) => void,
  coverType: any,
  quote: any,
) {
  function currencyStrToNum(currencyString: string) {
    return _.toNumber(_.replace(_.replace(currencyString, "AUD  ", ""), ",", ""));
  }
  // console.log("kakaka", coverType, quote,);
  let { premiums, promo } = quote;
  const pets = getFlowDataDirect("pets");

  setData(coverType + "Cover.promo", promo ? promo : null);
  setData(coverType + "Cover.premiums", premiums);

  // let installmentAmount = currencyStrToNum(premiums.installment);
  // setData(coverType + "Cover.installmentAmount", installmentAmount);

  // /* Brookes Addition */
  // let installmentBasePremium = currencyStrToNum(quote.installmentBasePremium);
  // setData(coverType + "Cover.installmentBasePremium", installmentBasePremium);
  // let installmentBasePremiumPlusGST = currencyStrToNum(quote.installmentBasePremiumPlusGST);
  // setData(coverType + "Cover.installmentBasePremiumPlusGST", installmentBasePremiumPlusGST);

  // let installmentPremiumGST = currencyStrToNum(quote.installmentPremiumGST);
  // setData(coverType + "Cover.installmentPremiumGST", installmentPremiumGST);
  // let installmentPolicyFeeGST = currencyStrToNum(quote.installmentPolicyFeeGST);
  // setData(coverType + "Cover.installmentPolicyFeeGST", installmentPolicyFeeGST);
  // setData(
  //   coverType + "Cover.installmentGST",
  //   installmentPremiumGST + installmentPolicyFeeGST
  // );

  // let installmentStampDuty = currencyStrToNum(quote.installmentStampDuty);
  // setData(coverType + "Cover.installmentStampDuty", installmentStampDuty);

  // let installmentPolicyFee = currencyStrToNum(quote.installmentPolicyFee);
  // setData(coverType + "Cover.installmentPolicyFee", installmentPolicyFee);
  // let installmentPolicyFeeTotal = currencyStrToNum(quote.installmentPolicyFeeTotal);
  // setData(coverType + "Cover.installmentPolicyFeeTotal", installmentPolicyFeeTotal);

  // let totalInstallmentBeforeDiscount = currencyStrToNum(
  //   quote.totalInstallmentBeforeDiscount
  // );
  // setData(
  //   coverType + "Cover.totalInstallmentBeforeDiscount",
  //   totalInstallmentBeforeDiscount
  // );

  // setData(
  //   coverType + "Cover.totalInstallmentDiscountAmount",
  //   totalInstallmentBeforeDiscount - installmentAmount
  // );

  // let totalPayable = currencyStrToNum(quote.totalPayable);
  // setData(coverType + "Cover.totalPayable", totalPayable);

  // let stampDuty = currencyStrToNum(quote.stampDuty);
  // setData(coverType + "Cover.stampDuty", stampDuty);

  // let premiumGST = currencyStrToNum(quote.premiumGST);
  // setData(coverType + "Cover.premiumGST", premiumGST);
  // let policyFeeGST = currencyStrToNum(quote.installmentPolicyFeeGST) * 12;
  // setData(coverType + "Cover.policyFeeGST", policyFeeGST);
  // // setData(coverType + "Cover.GST", premiumGST + policyFeeGST);
  // setData(coverType + "Cover.GST", premiumGST);

  // let commission = currencyStrToNum(quote.commission);
  // setData(coverType + "Cover.commission", commission);
  return { premiums, promo };
}

export async function sendSmsMessage(params: object): Promise<string> {
  let result: any;
  await sendSms(params)
    .then(function (response: any) {
      return response;
    })
    .then(function (data: any) {
      result = data;
    });

  return result;
}

export async function getPromocode(code: string): Promise<string> {
  let result: any;
  await getPromotionCode(code)
    .then(function (response: any) {
      return response;
    })
    .then(function (data: any) {
      result = data;
    });

  return result;
}

export async function sendKlaviyoEvent(data: any): Promise<string> {
  let result: any;
  await sendKlaviyoEvent(data)
    .then(function (response: any) {
      return response;
    })
    .then(function (dataReturn: any) {
      result = dataReturn;
    });

  return result;
}
